/**
 * Pure helpers for BookByPricePanel — extracted so they can be unit-tested
 * without React or DOM environments.
 */

export const STATUS = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  LIVE: "live",
  EMPTY: "empty",
  ERROR: "error",
});

/**
 * Compute panel status from snapshot + error.
 *
 * NOTE: there is deliberately no time-based "stale" status — an order book that
 * simply hasn't changed (illiquid options, etc.) is NOT stale, and flagging it
 * "Atrasado" produced false positives. The heuristic was removed (#333).
 * @param {object|null} snapshot  latest normalized snapshot or null
 * @param {string|null} symbol    currently subscribed symbol (null/empty = idle)
 * @param {Error|null} error      sticky error from the subscription
 * @returns {string} one of STATUS values
 */
export function computeBookStatus(snapshot, symbol, error) {
  if (error) return STATUS.ERROR;
  if (!symbol) return STATUS.IDLE;
  if (!snapshot) return STATUS.LOADING;
  const hasAnyLevels =
    (Array.isArray(snapshot.bids) && snapshot.bids.length > 0) ||
    (Array.isArray(snapshot.asks) && snapshot.asks.length > 0);
  if (!hasAnyLevels) return STATUS.EMPTY;
  return STATUS.LIVE;
}

/**
 * Limit bids/asks to the requested depth.
 * Returns new arrays; never mutates the snapshot.
 */
export function sliceDepth(snapshot, depth) {
  if (!snapshot) return { bids: [], asks: [] };
  const n = Number.isFinite(depth) && depth > 0 ? Math.floor(depth) : 10;
  return {
    bids: Array.isArray(snapshot.bids) ? snapshot.bids.slice(0, n) : [],
    asks: Array.isArray(snapshot.asks) ? snapshot.asks.slice(0, n) : [],
  };
}

/**
 * Best bid / best ask / spread / mid — display-only derived values.
 */
export function deriveTopOfBook(snapshot) {
  const bids = snapshot && Array.isArray(snapshot.bids) ? snapshot.bids : [];
  const asks = snapshot && Array.isArray(snapshot.asks) ? snapshot.asks : [];
  const bestBid =
    bids.find((b) => Number.isFinite(b?.price) && b.price > 0) || null;
  const bestAsk =
    asks.find((a) => Number.isFinite(a?.price) && a.price > 0) || null;
  const spread = bestBid && bestAsk ? bestAsk.price - bestBid.price : null;
  const midPrice =
    bestBid && bestAsk ? (bestBid.price + bestAsk.price) / 2 : null;
  return { bestBid, bestAsk, spread, midPrice };
}

/**
 * Convert a tick index back to a price value without floating-point drift.
 * Uses integer multiplier (1/tickSize) when tickSize < 1.
 */
function priceFromTickIdx(tickIdx, tickSize) {
  if (tickSize >= 1) return tickIdx * tickSize;
  const mult = Math.round(1 / tickSize);
  return tickIdx / mult;
}

/**
 * Build a FIXED-SIZE price grid for SuperDOM mode.
 *
 * Always returns exactly `rowCount` rows sorted descending by price.
 * Every tick-level in the grid has a row; levels with no orders have
 * bid: null / ask: null. This keeps the DOM stable across data updates:
 * React only updates text content, never inserts or removes rows.
 *
 * Grid center is stabilized via gridCenterRef: the center tick only moves
 * when the mid-price drifts more than rowCount/3 ticks from the current
 * center, matching TradezonePlatform's reposition threshold.
 *
 * Each row: { price, bid, ask, isBestBid, isBestAsk, isAnchor }
 *
 * @param {object|null} snapshot     normalized book snapshot
 * @param {number}      tickSize     instrument minimum price increment
 * @param {number}      rowCount     total rows to generate
 * @param {{ current: number|null }} gridCenterRef  persisted center tick index
 * @param {object} [options]
 * @param {boolean} [options.freezeCenter] do not normalize grid center while user is interacting
 */
export function buildFixedPriceLadder(
  snapshot,
  tickSize,
  rowCount,
  gridCenterRef,
  options = {},
) {
  if (!snapshot || !Number.isFinite(tickSize) || tickSize <= 0) return [];

  const bids = Array.isArray(snapshot.bids) ? snapshot.bids : [];
  const asks = Array.isArray(snapshot.asks) ? snapshot.asks : [];

  const bestBid =
    bids.find((b) => Number.isFinite(b?.price) && b.price > 0) ?? null;
  const bestAsk =
    asks.find((a) => Number.isFinite(a?.price) && a.price > 0) ?? null;

  if (!bestBid && !bestAsk) return [];

  const midPrice =
    bestBid && bestAsk
      ? (bestBid.price + bestAsk.price) / 2
      : (bestBid?.price ?? bestAsk.price);

  const midTick = Math.round(midPrice / tickSize);
  const driftThreshold = Math.floor(rowCount / 3);

  let centerTick = gridCenterRef?.current ?? midTick;
  if (!options.freezeCenter && Math.abs(midTick - centerTick) > driftThreshold)
    centerTick = midTick;
  if (gridCenterRef) gridCenterRef.current = centerTick;

  const topTick = centerTick + Math.ceil(rowCount / 2);

  const bidMap = new Map(
    bids
      .filter((b) => Number.isFinite(b?.price) && b.price > 0)
      .map((b) => [Math.round(b.price / tickSize), b]),
  );
  const askMap = new Map(
    asks
      .filter((a) => Number.isFinite(a?.price) && a.price > 0)
      .map((a) => [Math.round(a.price / tickSize), a]),
  );

  const bestBidTick = bestBid ? Math.round(bestBid.price / tickSize) : null;
  const bestAskTick = bestAsk ? Math.round(bestAsk.price / tickSize) : null;
  const anchorTick = bestBidTick ?? bestAskTick;

  const rows = [];
  for (let i = 0; i < rowCount; i++) {
    const tickIdx = topTick - i;
    rows.push({
      price: priceFromTickIdx(tickIdx, tickSize),
      bid: bidMap.get(tickIdx) ?? null,
      ask: askMap.get(tickIdx) ?? null,
      isBestBid: tickIdx === bestBidTick,
      isBestAsk: tickIdx === bestAskTick,
      isAnchor: tickIdx === anchorTick,
    });
  }

  return rows;
}

const hasApproxPrice = (rows, price) =>
  rows.some((row) => Math.abs(Number(row.price) - price) < 1e-9);

const syntheticAnnotationRow = (price) => ({
  price,
  bid: null,
  ask: null,
  isBestBid: false,
  isBestAsk: false,
  isAnchor: false,
  isSyntheticAnnotation: true,
});

/**
 * Adds display-only rows for annotations that are outside the fixed ladder.
 * Prices above the visible grid are prepended; prices below are appended.
 */
export function addSyntheticAnnotationRows(ladder, annotationsByPrice) {
  const rows = Array.isArray(ladder) ? ladder : [];
  const prices = Object.entries(annotationsByPrice || {})
    .filter(
      ([, annotations]) => Array.isArray(annotations) && annotations.length > 0,
    )
    .map(([key]) => Number(key))
    .filter((price) => Number.isFinite(price));

  if (!prices.length) return rows;

  if (!rows.length) {
    return prices.sort((a, b) => b - a).map(syntheticAnnotationRow);
  }

  const topPrice = Number(rows[0]?.price);
  const bottomPrice = Number(rows[rows.length - 1]?.price);
  if (!Number.isFinite(topPrice) || !Number.isFinite(bottomPrice)) return rows;

  const above = [];
  const below = [];

  prices.forEach((price) => {
    if (hasApproxPrice(rows, price)) return;
    if (price > topPrice) above.push(syntheticAnnotationRow(price));
    if (price < bottomPrice) below.push(syntheticAnnotationRow(price));
  });

  if (!above.length && !below.length) return rows;

  above.sort((a, b) => b.price - a.price);
  below.sort((a, b) => b.price - a.price);
  return [...above, ...rows, ...below];
}

/**
 * Pure positioning decision for SuperDOM auto-centering.
 * Returns 'recenter' | 'none'.
 *
 * Precedence (high → low):
 *   detached > edge override > pointer-freeze > drift dead-zone
 *
 * @param {object} p
 * @param {boolean} p.detached            user manually scrolled; auto-centering off
 * @param {boolean} p.pointerInside       pointer is currently over the scroll container
 * @param {number}  p.delta               anchorViewportCenter - usableCenter (px)
 * @param {number}  p.anchorViewportCenter anchor row center Y from container top (px)
 * @param {number}  p.headerH             sticky thead height (px)
 * @param {number}  p.clientHeight        scroll container visible height (px)
 * @param {number}  p.rowH                anchor row height (px)
 */
export function decidePositioning({
  pointerInside,
  delta,
  headerH,
  clientHeight,
}) {
  if (pointerInside) return "none";

  const deadZone = (clientHeight - headerH) / 4;
  if (Math.abs(delta) > deadZone) return "recenter";

  return "none";
}
